/**
 * CR226: Stable Preview Key Hook
 * 
 * Generates a stable key for preview components that:
 * - Changes when config actually changes
 * - Is debounced to prevent jitter during rapid edits
 * - Uses a checksum for efficiency
 * 
 * This prevents:
 * - Rapid remount loops
 * - "Try again" errors during editing
 * - Preview jitter
 */

import { useMemo, useRef, useEffect, useState } from "react";

// Simple string hash function (DJB2)
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return hash >>> 0; // Convert to unsigned
}

/**
 * Compute a stable hash from any config object
 */
/**
 * Compute a stable hash from any config object.
 * CR403: Hardened to never crash on undefined/null inputs.
 */
export function computeConfigHash(config: unknown): string {
  // CR403: JSON.stringify(undefined) => undefined which causes downstream .length crashes
  const safe = config === undefined ? null : config;
  const json = JSON.stringify(safe) ?? "null";
  const hash = hashString(json);
  return `${hash.toString(36)}-${json.length}`;
}

interface UseStablePreviewKeyOptions {
  /** Debounce delay in ms (default: 150) */
  debounceMs?: number;
  /** Prefix for the key */
  prefix?: string;
}

/**
 * Generate a stable, debounced preview key from config
 * 
 * @param config - Configuration object to hash
 * @param options - Options for debounce and prefix
 * @returns Stable preview key that only changes after debounce
 */
export function useStablePreviewKey(
  config: unknown,
  options: UseStablePreviewKeyOptions = {}
): {
  previewKey: string;
  configHash: string;
  isDebouncing: boolean;
} {
  const { debounceMs = 150, prefix = "preview" } = options;
  
  // Compute immediate hash
  const immediateHash = useMemo(() => computeConfigHash(config), [config]);
  
  // Track debounced hash
  const [debouncedHash, setDebouncedHash] = useState(immediateHash);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (immediateHash === debouncedHash) {
      setIsDebouncing(false);
      return;
    }
    
    setIsDebouncing(true);
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new debounced update
    timeoutRef.current = setTimeout(() => {
      setDebouncedHash(immediateHash);
      setIsDebouncing(false);
    }, debounceMs);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [immediateHash, debouncedHash, debounceMs]);
  
  const previewKey = `${prefix}-${debouncedHash}`;
  
  return {
    previewKey,
    configHash: immediateHash,
    isDebouncing,
  };
}

/**
 * Shorthand for computing just the hash (no debounce)
 */
export function useConfigHash(config: unknown): string {
  return useMemo(() => computeConfigHash(config), [config]);
}
